/**
 * Connect Source — Centralized Product Catalog Configuration
 *
 * Strict Architecture Rules:
 * 1. Do NOT hardcode prices throughout the UI.
 * 2. Five paid digital products with exact designated INR pricing.
 * 3. Free lead magnet ('Interview Preparation Starter Guide') is strictly separated
 *    and NEVER conflated with the paid 'Interview Cracker Kit'.
 * 4. International pricing is null/not invented until officially set.
 * 5. File references contain secure key identifiers, never public direct download URLs.
 * 6. Every file in a paid product belongs only to that specific product.
 * 7. A successful purchase grants access only to the files belonging to that purchased product.
 */

import { Product } from '../types/product';

export const ATS_RESUME_MASTER_KIT_SLUG = 'ats-resume-master-kit';
export const LINKEDIN_TOOLKIT_SLUG = 'linkedin-profile-optimization-toolkit';
export const INTERVIEW_CRACKER_KIT_SLUG = 'interview-cracker-kit';
export const AI_JOB_SEARCH_TOOLKIT_SLUG = 'ai-job-search-toolkit';
export const JOB_SWITCH_BLUEPRINT_SLUG = '30-day-job-switch-blueprint';
export const FREE_INTERVIEW_GUIDE_SLUG = 'free-interview-guide';

export const PRODUCT_CATALOG: readonly Product[] = [
  // 1. ATS Resume Master Kit (Paid)
  {
    id: 'prod_ats_resume_01',
    slug: ATS_RESUME_MASTER_KIT_SLUG,
    title: 'ATS Resume Master Kit',
    subtitle:
      'Practical resume templates designed to help you structure your resume clearly and consistently.',
    type: 'paid_product',
    priceINR: 199,
    pricing: {
      amount: 199,
      currency: 'INR',
      displayAmount: '₹199',
    },
    internationalPricing: {
      USD: null,
      EUR: null,
      GBP: null,
    },
    deliverableType: 'templates_bundle',
    files: [
      {
        id: 'file_ats_master_kit',
        fileName: 'ATS_Resume_Master_Kit.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/ats-resume-master-kit/ATS_Resume_Master_Kit.pdf',
      },
      {
        id: 'file_ats_fresher',
        fileName: 'Fresher_Resume_Template.docx',
        fileFormat: 'docx',
        version: '1.0.0',
        storagePathKey:
          'products/ats-resume-master-kit/Fresher_Resume_Template.docx',
      },
      {
        id: 'file_ats_experienced',
        fileName: 'Experienced_Resume_Template.docx',
        fileFormat: 'docx',
        version: '1.0.0',
        storagePathKey:
          'products/ats-resume-master-kit/Experienced_Resume_Template.docx',
      },
      {
        id: 'file_ats_career_switch',
        fileName: 'CareerSwitch_Resume_Template.docx',
        fileFormat: 'docx',
        version: '1.0.0',
        storagePathKey:
          'products/ats-resume-master-kit/CareerSwitch_Resume_Template.docx',
      },
    ],
    features: [
      'ATS Resume Master Kit - PDF',
      'Fresher Resume Template - DOCX',
      'Experienced Resume Template - DOCX',
      'Career Switch Resume Template - DOCX',
    ],
    targetAudience: [
      'Job seekers who want a structured resume format',
      'Working professionals updating their resume',
      'Career switchers preparing a resume for a career transition',
    ],
    ctaText: 'Get this product for ₹199',
    routePath: `/digital-products/${ATS_RESUME_MASTER_KIT_SLUG}`,
    isFeatured: true,
    isActive: true,
    requiresShipping: false,
    metadata: {
      category: 'Resume & Branding',
      readingTimeMinutes: 45,
    },
  },

  // 2. LinkedIn Profile Optimization Toolkit (Paid)
  {
    id: 'prod_linkedin_opt_02',
    slug: LINKEDIN_TOOLKIT_SLUG,
    title: 'LinkedIn Profile Optimization Toolkit',
    subtitle:
      'Transform your profile into an inbound recruiter magnet with headline formulas, about section blueprints, and visibility strategies.',
    type: 'paid_product',
    priceINR: 499,
    pricing: {
      amount: 499,
      currency: 'INR',
      displayAmount: '₹499',
    },
    internationalPricing: {
      USD: null,
      EUR: null,
      GBP: null,
    },
    deliverableType: 'toolkit',
    files: [
      {
        id: 'file_linkedin_headline_docx',
        fileName: 'LinkedIn_Headline_Templates.docx',
        fileFormat: 'docx',
        version: '1.0.0',
        storagePathKey:
          'products/linkedin-profile-optimization-toolkit/LinkedIn_Headline_Templates.docx',
      },
      {
        id: 'file_linkedin_headline_pdf',
        fileName: 'LinkedIn_Headline_Templates.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/linkedin-profile-optimization-toolkit/LinkedIn_Headline_Templates.pdf',
      },
      {
        id: 'file_linkedin_about_template',
        fileName: 'About_Section_Template.docx',
        fileFormat: 'docx',
        version: '1.0.0',
        storagePathKey:
          'products/linkedin-profile-optimization-toolkit/About_Section_Template.docx',
      },
      {
        id: 'file_linkedin_about_samples',
        fileName: 'About_Section_Samples.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/linkedin-profile-optimization-toolkit/About_Section_Samples.pdf',
      },
      {
        id: 'file_linkedin_checklist',
        fileName: 'LinkedIn_Profile_Checklist.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/linkedin-profile-optimization-toolkit/LinkedIn_Profile_Checklist.pdf',
      },
      {
        id: 'file_linkedin_content',
        fileName: 'Content_Post_Ideas.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/linkedin-profile-optimization-toolkit/Content_Post_Ideas.pdf',
      },
      {
        id: 'file_linkedin_networking',
        fileName: 'Networking_Message_Templates.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/linkedin-profile-optimization-toolkit/Networking_Message_Templates.pdf',
      },
      {
        id: 'file_linkedin_guide',
        fileName: 'Profile_Optimization_Guide.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/linkedin-profile-optimization-toolkit/Profile_Optimization_Guide.pdf',
      },
      {
        id: 'file_linkedin_before_after',
        fileName: 'Before_vs_After_Examples.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/linkedin-profile-optimization-toolkit/Before_vs_After_Examples.pdf',
      },
    ],
    features: [
      'LinkedIn Headline Templates - DOCX + PDF',
      'About Section Template - DOCX',
      'About Section Samples - PDF',
      'LinkedIn Profile Checklist - PDF',
      'Content Post Ideas - PDF',
      'Networking Message Templates - PDF',
      'Profile Optimization Guide - PDF',
      'Before vs After Examples - PDF',
    ],
    targetAudience: [
      'Professionals wanting passive inbound recruiter inquiries',
      'Leaders looking to establish credible industry presence',
      'Graduates and early-stage professionals building network traction',
    ],
    ctaText: 'Get this product for ₹499',
    routePath: `/digital-products/${LINKEDIN_TOOLKIT_SLUG}`,
    isFeatured: true,
    isActive: true,
    requiresShipping: false,
    metadata: {
      category: 'Personal Branding',
      framework: 'Inbound Positioning',
    },
  },

  // 3. Interview Cracker Kit (Job Search Edition) (Paid)
  // NOTE: This is the PAID comprehensive kit, strictly distinct from the free starter guide.
  {
    id: 'prod_interview_cracker_03',
    slug: INTERVIEW_CRACKER_KIT_SLUG,
    title: 'Interview Cracker Kit (Job Search Edition)',
    subtitle:
      'Practical interview preparation resources covering questions, HR guidance, interview strategy, follow-up, answer frameworks, confidence support, and before-vs-after examples.',
    type: 'paid_product',
    priceINR: 399,
    pricing: {
      amount: 399,
      currency: 'INR',
      displayAmount: '₹399',
    },
    internationalPricing: {
      USD: null,
      EUR: null,
      GBP: null,
    },
    deliverableType: 'toolkit',
    files: [
      {
        id: 'file_interview_quick_start',
        fileName: 'Quick Start Guide.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/interview-cracker-kit/Quick Start Guide.pdf',
      },
      {
        id: 'file_interview_questions',
        fileName: 'Top Interview Questions & Answers.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/interview-cracker-kit/Top Interview Questions & Answers.pdf',
      },
      {
        id: 'file_interview_hr',
        fileName: 'HR Questions Guide.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/interview-cracker-kit/HR Questions Guide.pdf',
      },
      {
        id: 'file_interview_strategy',
        fileName: 'Interview Strategy Guide.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/interview-cracker-kit/Interview Strategy Guide.pdf',
      },
      {
        id: 'file_interview_follow_up',
        fileName: 'Follow-Up Email Template.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/interview-cracker-kit/Follow-Up Email Template.pdf',
      },
      {
        id: 'file_interview_donts',
        fileName: 'Interview Do’s & Don’ts Checklist.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/interview-cracker-kit/Interview Do’s & Don’ts Checklist.pdf',
      },
      {
        id: 'file_interview_framework',
        fileName: 'Answer Framework Cheat Sheet.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/interview-cracker-kit/Answer Framework Cheat Sheet.pdf',
      },
      {
        id: 'file_interview_confidence',
        fileName: 'Confidence Booster Script.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/interview-cracker-kit/Confidence Booster Script.pdf',
      },
      {
        id: 'file_interview_before_after',
        fileName: 'Before vs After Answer Example.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/interview-cracker-kit/Before vs After Answer Example.pdf',
      },
    ],
    features: [
      'Quick Start Guide - PDF',
      'Top Interview Questions & Answers - PDF',
      'HR Questions Guide - PDF',
      'Interview Strategy Guide - PDF',
      'Follow-Up Email Template - PDF',
      "Interview Do’s & Don’ts Checklist - PDF",
      'Answer Framework Cheat Sheet - PDF',
      'Confidence Booster Script - PDF',
      'Before vs After Answer Example - PDF',
    ],
    targetAudience: [
      'Candidates preparing for interviews',
      'Students, freshers, working professionals, job seekers, and career switchers',
      'Job seekers looking for structured interview preparation and follow-up guidance',
    ],
    ctaText: 'Get this product for ₹399',
    routePath: `/digital-products/${INTERVIEW_CRACKER_KIT_SLUG}`,
    isFeatured: false,
    isActive: true,
    requiresShipping: false,
    metadata: {
      category: 'Interview Preparation',
      edition: 'Job Search Edition',
    },
  },

  // 4. AI Job Search Toolkit (Paid)
  {
    id: 'prod_ai_job_search_04',
    slug: AI_JOB_SEARCH_TOOLKIT_SLUG,
    title: 'AI Job Search Toolkit',
    subtitle:
      'Practical AI-assisted resources for resume writing, cover letters, LinkedIn messaging, job application tracking, and AI tool discovery.',
    type: 'paid_product',
    priceINR: 699,
    pricing: {
      amount: 699,
      currency: 'INR',
      displayAmount: '₹699',
    },
    internationalPricing: {
      USD: null,
      EUR: null,
      GBP: null,
    },
    deliverableType: 'toolkit',
    files: [
      {
        id: 'file_ai_resume_prompts',
        fileName: 'AI Resume Prompts.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/ai-job-search-toolkit/AI Resume Prompts.pdf',
      },
      {
        id: 'file_ai_cover_letter',
        fileName: 'Cover Letter Prompts.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/ai-job-search-toolkit/Cover Letter Prompts.pdf',
      },
      {
        id: 'file_ai_tracker_pdf',
        fileName: 'Job Application Tracker.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/ai-job-search-toolkit/Job Application Tracker.pdf',
      },
      {
        id: 'file_ai_tracker',
        fileName: 'Job_Application_Tracker_CS.xlsx',
        fileFormat: 'xlsx',
        version: '1.0.0',
        storagePathKey:
          'products/ai-job-search-toolkit/Job_Application_Tracker_CS.xlsx',
      },
      {
        id: 'file_ai_linkedin',
        fileName: 'LinkedIn Messaging Prompts.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/ai-job-search-toolkit/LinkedIn Messaging Prompts.pdf',
      },
      {
        id: 'file_ai_quick_start',
        fileName: 'Quick Start Guide.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/ai-job-search-toolkit/Quick Start Guide.pdf',
      },
      {
        id: 'file_ai_tools',
        fileName: 'Top AI Tools.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/ai-job-search-toolkit/Top AI Tools.pdf',
      },
    ],
    features: [
      'AI Resume Prompts - PDF',
      'Cover Letter Prompts - PDF',
      'Job Application Tracker - PDF',
      'Job Application Tracker - Excel',
      'LinkedIn Messaging Prompts - PDF',
      'Quick Start Guide - PDF',
      'Top AI Tools - PDF',
    ],
    targetAudience: [
      'Job seekers who want practical AI support during their job search',
      'Students, freshers, working professionals, job seekers, and career switchers',
      'Job seekers who want structured tools for applications, messaging, and tracking',
    ],
    ctaText: 'Get this product for ₹699',
    routePath: `/digital-products/${AI_JOB_SEARCH_TOOLKIT_SLUG}`,
    isFeatured: true,
    isActive: true,
    requiresShipping: false,
    metadata: {
      category: 'AI & Productivity',
      version: '2025',
    },
  },

  // 5. 30-Day Job Switch Blueprint (Paid)
  {
    id: 'prod_job_switch_05',
    slug: JOB_SWITCH_BLUEPRINT_SLUG,
    title: '30-Day Job Switch Blueprint',
    subtitle:
      'The complete end-to-end career transition roadmap: daily action plans, networking cadence, and pipeline management.',
    type: 'paid_product',
    priceINR: 999,
    pricing: {
      amount: 999,
      currency: 'INR',
      displayAmount: '₹999',
    },
    internationalPricing: {
      USD: null,
      EUR: null,
      GBP: null,
    },
    deliverableType: 'system_blueprint',
        files: [
      {
        id: 'file_job_switch_quick_start',
        fileName: 'Quick_Start_Guide.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/30-day-job-switch-blueprint/Quick_Start_Guide.pdf',
      },
      {
        id: 'file_job_switch_30_day',
        fileName: '30_Day_Action_Plan.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/30-day-job-switch-blueprint/30_Day_Action_Plan.pdf',
      },
      {
        id: 'file_job_switch_daily_pdf',
        fileName: 'Daily_Checklist_Templates.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/30-day-job-switch-blueprint/Daily_Checklist_Templates.pdf',
      },
      {
        id: 'file_job_switch_gamified',
        fileName: 'Gamified_30_Day_Job_Search_Challenge_CS.xlsx',
        fileFormat: 'xlsx',
        version: '1.0.0',
        storagePathKey:
          'products/30-day-job-switch-blueprint/Gamified_30_Day_Job_Search_Challenge_CS.xlsx',
      },
      {
        id: 'file_job_switch_tracker',
        fileName: 'Job_Application_Tracker_Professional_CS.xlsx',
        fileFormat: 'xlsx',
        version: '1.0.0',
        storagePathKey:
          'products/30-day-job-switch-blueprint/Job_Application_Tracker_Professional_CS.xlsx',
      },
      {
        id: 'file_job_switch_planner',
        fileName: 'Premium_Job_Search_Planner_30_Day_CS.xlsx',
        fileFormat: 'xlsx',
        version: '1.0.0',
        storagePathKey:
          'products/30-day-job-switch-blueprint/Premium_Job_Search_Planner_30_Day_CS.xlsx',
      },
      {
        id: 'file_job_switch_weekly',
        fileName: 'Weekly_Strategy_Guides.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/30-day-job-switch-blueprint/Weekly_Strategy_Guides.pdf',
      },
      {
        id: 'file_job_switch_salary',
        fileName: 'Salary_Negotiation_Script.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/30-day-job-switch-blueprint/Salary_Negotiation_Script.pdf',
      },
      {
        id: 'file_job_switch_follow_up',
        fileName: 'Follow-Up_Message_Templates.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/30-day-job-switch-blueprint/Follow-Up_Message_Templates.pdf',
      },
      {
        id: 'file_job_switch_reality_trust',
        fileName: 'Reality_Trust_Section.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey:
          'products/30-day-job-switch-blueprint/Reality_Trust_Section.pdf',
      },
    ],
    features: [
      '30-Day Action Plan - PDF',
      'Daily Checklist Templates - PDF',
      'Follow-Up Message Templates - PDF',
      'Gamified 30-Day Job Search Challenge - Excel',
      'Job Application Tracker Professional - Excel',
      'Premium Job Search Planner 30-Day - Excel',
      'Quick Start Guide - PDF',
      'RealityTrust Section - PDF',
      'Salary Negotiation Script - PDF',
      'Weekly Strategy Guides - PDF',
    ],
    targetAudience: [
      'Employed professionals who want an organized transition without burnout',
      'Career pivoters targeting a higher salary bracket or domain switch',
      'Job seekers needing disciplined daily momentum and structure',
    ],
    ctaText: 'Get this product for ₹999',
    routePath: `/digital-products/${JOB_SWITCH_BLUEPRINT_SLUG}`,
    isFeatured: true,
    isActive: true,
    requiresShipping: false,
    metadata: {
      category: 'Career Transformation Strategy',
      durationDays: 30,
    },
  },

  // FREE LEAD MAGNET: Interview Preparation Starter Guide
  // CRITICAL DISTINCTION: This is a separate free starter guide for lead generation.
  // It is NEVER conflated with the paid Interview Cracker Kit.
  {
    id: 'lead_interview_starter_00',
    slug: FREE_INTERVIEW_GUIDE_SLUG,
    title: 'Interview Preparation Starter Guide',
    subtitle:
      'Essential foundational checklist, standard STAR response structure, and 10 critical interview preparation tips.',
    type: 'free_lead_magnet',
    priceINR: 0,
    pricing: {
      amount: 0,
      currency: 'INR',
      displayAmount: 'FREE',
    },
    internationalPricing: {
      USD: null,
      EUR: null,
      GBP: null,
    },
    deliverableType: 'pdf_guide',
    files: [
      {
        id: 'file_free_starter_guide_v1',
        fileName: 'Interview_Preparation_Starter_Guide_Free.pdf',
        fileFormat: 'pdf',
        version: '1.0.0',
        storagePathKey: 'free-guides/interview_prep_starter_guide.pdf',
      },
    ],
    features: [
      'Core 10-point pre-interview readiness checklist',
      'The foundational STAR framework template',
      'Top 5 rookie interview mistakes to avoid',
      'Day-of-interview confidence preparation protocol',
    ],
    targetAudience: [
      'Anyone preparing for their first interviews in a new season',
      'Professionals brushing up on fundamentals before scheduling mocks',
      'New graduates entering corporate interview pipelines',
    ],
    ctaText: 'Download Free Starter Guide',
    routePath: '/free-interview-guide',
    isFeatured: false,
    isActive: true,
    requiresShipping: false,
    metadata: {
      category: 'Free Resource',
      isLeadMagnet: true,
    },
  },
];

// Helper accessors

export function getAllProducts(): readonly Product[] {
  return PRODUCT_CATALOG;
}

export function getPaidProducts(): Product[] {
  return PRODUCT_CATALOG.filter(
    (p) => p.type === 'paid_product' && p.isActive,
  );
}

export function getLeadMagnets(): Product[] {
  return PRODUCT_CATALOG.filter(
    (p) => p.type === 'free_lead_magnet' && p.isActive,
  );
}

export function getProductBySlug(slug: string): Product | undefined {
  return PRODUCT_CATALOG.find((p) => p.slug === slug && p.isActive);
}

export function isPaidProduct(product: Product): boolean {
  return product.type === 'paid_product';
}

export function isLeadMagnet(product: Product): boolean {
  return product.type === 'free_lead_magnet';
}

export function formatPriceINR(amount: number): string {
  if (amount === 0) return 'FREE';
  return `₹${amount.toLocaleString('en-IN')}`;
}